import React, { useRef, useState } from "react";

/* ── Types ─────────────────────────────────────────────────── */

export interface CarouselSlide {
  url: string;
  type: string; // "IMAGE" | "VIDEO"
}

export interface CarouselSlideStripProps {
  /** Ordered list of slides to display. */
  slides: CarouselSlide[];
  /** Maximum number of slides allowed (default 10). */
  maxSlides?: number;
  /** Minimum slides needed for a valid carousel (default 2). Shows a hint. */
  minPublishSlides?: number;
  /** Platform named in the minimum-slides hint (default "Instagram"). */
  platformLabel?: string;
  /** Currently active (hero) slide index — controlled by parent. */
  activeIndex: number;
  /** Called when the active slide changes. */
  onActiveIndexChange: (index: number) => void;
  /** If provided, each thumbnail gets a × button that calls this. */
  onRemoveSlide?: (index: number) => void;
  /** If provided, the hero image gets a × button that removes the active slide. */
  onRemoveHero?: () => void;
  /** If provided, thumbnails are draggable and this fires on drop. */
  onReorder?: (fromIndex: number, toIndex: number) => void;
  /** If provided, a "+" button appears at the end of the strip. */
  onAddSlide?: () => void;
  /**
   * Custom ReactNode rendered inside the add-slide slot.
   * Use this to inject a popover/menu (e.g. Canvas upload menu).
   * If omitted but `onAddSlide` is provided, a default "+" button renders.
   */
  addSlideContent?: React.ReactNode;
  /** Extra class on the outer wrapper. */
  className?: string;
  /** When true, renders only the hero + dots — no thumbnail editing strip. */
  readOnly?: boolean;
}

/* ── Constants ─────────────────────────────────────────────── */

const DEFAULT_MAX_SLIDES = 10;
const DEFAULT_MIN_PUBLISH_SLIDES = 2;

/* ── Component ─────────────────────────────────────────────── */

const CarouselSlideStrip: React.FC<CarouselSlideStripProps> = ({
  slides,
  maxSlides = DEFAULT_MAX_SLIDES,
  minPublishSlides = DEFAULT_MIN_PUBLISH_SLIDES,
  platformLabel = "Instagram",
  activeIndex,
  onActiveIndexChange,
  onRemoveSlide,
  onRemoveHero,
  onReorder,
  onAddSlide,
  addSlideContent,
  className = "",
  readOnly = false,
}) => {
  /* ---- drag state ---- */
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const thumbStripRef = useRef<HTMLDivElement>(null);
  const activeThumbRef = useRef<HTMLDivElement>(null);

  const clampedIndex = Math.min(activeIndex, slides.length - 1);
  const hero = slides[clampedIndex];
  const heroIsVideo =
    hero && hero.type?.toUpperCase() === "VIDEO";

  /* ---- drag handlers ---- */
  const handleDragStart = (index: number) => setDragFrom(index);
  const handleDragEnd = () => {
    setDragFrom(null);
    setDragOverIndex(null);
  };
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragFrom !== null && dragFrom !== index) {
      setDragOverIndex(index);
    }
  };
  const handleDragLeave = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragOverIndex === index) {
      setDragOverIndex(null);
    }
  };
  const handleDropOnThumb = (toIndex: number) => {
    setDragOverIndex(null);
    setDragFrom(null);
    if (dragFrom == null || dragFrom === toIndex || !onReorder) return;
    onReorder(dragFrom, toIndex);
    onActiveIndexChange(toIndex);
  };

  /* ---- scroll helpers ---- */
  const scrollThumbs = (dir: -1 | 1) => {
    if (!thumbStripRef.current) return;
    thumbStripRef.current.scrollBy({ left: dir * 200, behavior: "smooth" });
  };

  /* ---- auto-scroll effect ---- */
  React.useEffect(() => {
    if (activeThumbRef.current && thumbStripRef.current) {
      const thumb = activeThumbRef.current;
      const strip = thumbStripRef.current;
      
      const thumbLeft = thumb.offsetLeft;
      const thumbWidth = thumb.offsetWidth;
      const stripWidth = strip.clientWidth;
      
      const scrollPos = thumbLeft - (stripWidth / 2) + (thumbWidth / 2);
      
      strip.scrollTo({
        left: scrollPos,
        behavior: "smooth"
      });
    }
  }, [activeIndex]);

  if (slides.length === 0) return null;

  return (
    <div className={`flex w-full flex-col ${className}`}>
      {/* ── Hero View ─────────────────────────────────────── */}
      <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-black">
        {heroIsVideo ? (
          <video
            src={hero.url}
            controls
            className="h-full w-full object-contain"
          />
        ) : (
          // contain, not cover: never crop what we publish (original file is sent).
          // Instagram itself crops carousel slides to slide 1's ratio (SGL-435 follow-up).
          <img
            src={hero?.url}
            alt=""
            className="h-full w-full object-contain"
          />
        )}

        {/* Dots overlay */}
        {slides.length > 1 && (
          <div className="pointer-events-none absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Slide ${i + 1}`}
                className={`pointer-events-auto h-1.5 rounded-full transition-all ${
                  i === clampedIndex
                    ? "w-5 bg-white"
                    : "w-1.5 bg-white/40"
                }`}
                onClick={() => onActiveIndexChange(i)}
              />
            ))}
          </div>
        )}

        {onRemoveHero && !readOnly && (
          <button
            type="button"
            onClick={onRemoveHero}
            className="absolute right-3 top-3 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white shadow-lg backdrop-blur-sm transition-all hover:bg-theme-danger hover:scale-110 active:scale-95"
            title="Remove this slide"
            aria-label="Remove this slide from carousel"
          >
            <span className="material-icons text-xl">close</span>
          </button>
        )}
      </div>

      {/* ── Thumbnail Strip ───────────────────────────────── */}
      {!readOnly && (
        <div className="border-b border-theme-border/30 bg-theme-background/60 px-2 py-3">
          <div className="flex items-center gap-2">
            {/* Left scroll arrow */}
            <button
              type="button"
              onClick={() => scrollThumbs(-1)}
              className="flex h-20 w-8 shrink-0 items-center justify-center rounded-xl bg-theme-surface/50 text-theme-fg-muted shadow-sm transition-all hover:bg-theme-surface hover:text-theme-fg"
              aria-label="Scroll thumbnails left"
            >
              <span className="material-icons text-2xl">chevron_left</span>
            </button>

            {/* Scrollable thumbnails */}
            <div
              ref={thumbStripRef}
              className="custom-scrollbar relative flex min-w-0 flex-1 items-center gap-2 overflow-x-auto pb-2"
            >
              {slides.map((slide, index) => (
                <div
                  key={`${slide.url}-${index}`}
                  ref={index === clampedIndex ? activeThumbRef : null}
                  draggable={!!onReorder}
                  onDragStart={() => handleDragStart(index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={(e) => handleDragLeave(e, index)}
                  onDrop={() => handleDropOnThumb(index)}
                  className={`relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border-2 transition-all duration-200 ease-in-out ${
                    onReorder ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
                  } ${
                    dragFrom === index
                      ? "opacity-40 scale-90"
                      : dragOverIndex === index
                      ? "border-brand-primary/80 ring-4 ring-brand-primary/20 scale-105 z-10"
                      : index === clampedIndex
                      ? "border-theme-primary ring-2 ring-theme-primary/20 scale-95"
                      : "border-transparent opacity-80 hover:opacity-100 focus:scale-100"
                  }`}
                  onClick={() => onActiveIndexChange(index)}
                >
                  {slide.type?.toUpperCase() === "VIDEO" ? (
                    <video
                      src={slide.url}
                      muted
                      playsInline
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <img
                      src={slide.url}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  )}
                  {/* Slide number badge */}
                  <span className="absolute bottom-1 left-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-lg bg-black/60 px-1 text-[11px] font-bold text-white backdrop-blur-sm">
                    {index + 1}
                  </span>
                  {/* Slide remove button */}
                  {onRemoveSlide && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveSlide(index);
                      }}
                      className="absolute right-1 top-1 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white shadow-sm backdrop-blur-sm transition-colors hover:bg-theme-danger"
                      title="Remove slide"
                      aria-label="Remove slide from carousel"
                    >
                      <span className="material-icons text-[16px] leading-none">
                        close
                      </span>
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add slide slot */}
            {slides.length < maxSlides && (onAddSlide || addSlideContent) && (
              <div className="shrink-0 flex items-center">
                {addSlideContent ? (
                  addSlideContent
                ) : (
                  <button
                    type="button"
                    onClick={onAddSlide}
                    className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border-2 border-dashed border-theme-border bg-theme-surface text-theme-fg-muted transition-all hover:border-theme-primary hover:bg-theme-primary/5 hover:text-theme-primary"
                    title="Add slide"
                  >
                    <span className="material-icons text-3xl">add</span>
                  </button>
                )}
              </div>
            )}

            {/* Right scroll arrow */}
            <button
              type="button"
              onClick={() => scrollThumbs(1)}
              className="flex h-20 w-8 shrink-0 items-center justify-center rounded-xl bg-theme-surface/50 text-theme-fg-muted shadow-sm transition-all hover:bg-theme-surface hover:text-theme-fg"
              aria-label="Scroll thumbnails right"
            >
              <span className="material-icons text-2xl">chevron_right</span>
            </button>
          </div>

          {/* Minimum slides hint */}
          {slides.length < minPublishSlides && (
            <p className="px-2 pt-1 text-[11px] text-theme-fg-muted">
              Add at least {minPublishSlides - slides.length} more slide
              {minPublishSlides - slides.length > 1 ? "s" : ""} for a published
              {platformLabel} carousel ({minPublishSlides}–{maxSlides} items).
            </p>
          )}
        </div>
      )}

      {/* ── Read-only:  just show slide counter ─────────── */}
      {readOnly && slides.length > 1 && (
        <div className="flex items-center justify-center gap-1 py-1.5 text-xs text-theme-fg-muted">
          <span className="material-icons text-sm">view_carousel</span>
          {slides.length} slides
        </div>
      )}
    </div>
  );
};

export default CarouselSlideStrip;
